import {
  SpaceEventAction,
  SpaceEventType,
  SpaceScanStatus,
} from "../../../passapp-sdk/types/spaceTypes";
import { ReservationResultStatusVariant } from "../components/ReservationResultStatus/ReservationResultStatus";

// Cada status del scan mapea uno a uno con una pantalla.
export type ReservationResultKey = SpaceScanStatus;

export type ReservationResultNoticeData = {
  title: string;
  description: string;
};

export type ReservationResultCopy = {
  variant: ReservationResultStatusVariant;
  title: string;
  notice?: ReservationResultNoticeData;
};

export const RESERVATION_RESULTS: Record<ReservationResultKey, ReservationResultCopy> = {
  valid: {
    variant: "success",
    title: "Acceso permitido",
  },
  noReservation: {
    variant: "error",
    title: "Sin reserva para hoy",
    notice: {
      title: "Sin reserva activa.",
      description:
        "No le corresponde la opción del día. Elegí si le asignás el snack o rechazás el ingreso: queda registrado a tu nombre.",
    },
  },
  outOfRange: {
    variant: "warning",
    title: "Fuera de horario",
    notice: {
      title: "Verificá el horario.",
      description:
        "Tiene reserva para hoy, pero llegó fuera de su franja. Si lo dejás pasar, queda registrado a tu nombre.",
    },
  },
  alreadyUsed: {
    variant: "warning",
    title: "Ya ingresó hoy",
    notice: {
      title: "Reserva ya utilizada.",
      description:
        "Esta persona ya hizo check-in hoy. Si lo dejás pasar de nuevo, el ingreso queda registrado a tu nombre.",
    },
  },
  invalidQr: {
    variant: "error",
    title: "QR no válido",
  },
};

export const RESERVATION_ACTION_LABELS: Record<SpaceEventAction, string> = {
  reject: "Rechazar acceso",
  alternative: "Asignar snack",
  checkin: "Permitir acceso",
};

// Botón que se le ofrece al operador. El texto no siempre sale de la acción: con la reserva
// ya consumida "Permitir acceso" manda `alternative`, que es lo único que admite el servidor.
export type ReservationResultAction = {
  action: SpaceEventAction;
  label: string;
  /** El botón de asignar el snack lleva ícono. */
  icon?: "snack";
};

export type ReservationActionResult = {
  variant: Extract<ReservationResultStatusVariant, "success" | "error">;
  title: string;
  description: string;
};

// El modal confirma lo que el servidor registró, no lo que el operador tocó: el
// caso se resuelve de nuevo al recibir la decisión.
export const RESERVATION_EVENT_RESULTS: Record<SpaceEventType, ReservationActionResult> = {
  reject: {
    variant: "error",
    title: "Acceso rechazado",
    description: "El ingreso quedó registrado como rechazado, con su motivo.",
  },
  alternative: {
    variant: "success",
    title: "Snack asignado",
    description: "Se registró la entrega del snack. No genera reserva.",
  },
  checkin: {
    variant: "success",
    title: "Acceso permitido",
    description: "Ingreso autorizado y registrado a tu nombre. Se consumió su reserva.",
  },
};

// La alternativa la nombra el admin del espacio (ej. "Fruta"): el evento registrado trae ese nombre
// en `optionName`. Sin nombre queda el genérico "Snack". Cuando el operador la pidió con
// "Permitir acceso" (reserva ya consumida), el título confirma el acceso y el cuerpo, lo que se entrega.
export const alternativeActionResult = (
  optionName?: string | null,
  accessGranted = false
): ReservationActionResult => {
  const name = optionName?.trim();
  if (accessGranted) {
    return {
      variant: "success",
      title: "Acceso concedido",
      description: name
        ? `Ingreso autorizado y registrado a tu nombre. Se entregó ${name}: no genera reserva.`
        : "Ingreso autorizado y registrado a tu nombre. Se entregó el snack: no genera reserva.",
    };
  }
  if (!name) return RESERVATION_EVENT_RESULTS.alternative;
  return {
    variant: "success",
    title: `Se asignó ${name}`,
    description: `Se registró la entrega de ${name}. No genera reserva.`,
  };
};

// Fallback cuando la respuesta no trae evento: mostramos lo que se pidió.
export const RESERVATION_ACTION_EVENT_TYPE: Record<SpaceEventAction, SpaceEventType> = {
  reject: "reject",
  alternative: "alternative",
  checkin: "checkin",
};
